import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { TranslateService } from "@ngx-translate/core";

import { Store } from "@ngrx/store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { from } from "rxjs";
import { exhaustMap, map, catchError, switchMap, tap, mergeMap } from "rxjs/operators";

import * as MessagesActionTypes from "../../actions/messages/message.actions";
import { AppState } from "../../reducers";
import * as MovementsActionTypes from "../../actions/movements/movements.actions";

import { MovementService } from "src/app/shared/services/movements/movement.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";

import { SnackBarUtils } from "src/app/shared/utils/snackbarUtils";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";

@Injectable()
export class MovementEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private router: Router,
    private translate: TranslateService,
    private movementService: MovementService,
    private tableConfigService: TableConfigService
  ) {}

  getMovementsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MovementsActionTypes.getContractMovementsList),
      switchMap((action) =>
        this.movementService
          .getMovements(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) => {
              result.timestamp = new Date();
              result?.result?.items?.map((movement) => {
                if (movement?.debitMovement) {
                  movement.debitMovement.n_cards = movement?.debitMovement?.card
                    ? Object.keys(movement?.debitMovement?.card).length
                    : "0";
                }
              });
              return MovementsActionTypes.loadContractMovementsPartyList({
                response: result,
              });
            }),
            catchError((error) => {
              const actions = [
                MovementsActionTypes.loadContractMovementsPartyList({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("Movements.LoadMovementsError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getMovement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MovementsActionTypes.getMovement),
      exhaustMap((action) =>
        this.movementService
          .getMovement(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              MovementsActionTypes.loadMovement({ response: result?.result })
            ),
            catchError((error) => {
              const actions = [
                MovementsActionTypes.loadMovement({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("Movements.LoadError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getMovementsExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MovementsActionTypes.getMovementsExcelList),
      exhaustMap((action) =>
        this.movementService
          .getMovements(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              MovementsActionTypes.loadMovementsExcelList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                MovementsActionTypes.loadMovementsExcelList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("Movements.loadError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Movements Table Config
  getMovementsTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MovementsActionTypes.getMovementTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              MovementsActionTypes.loadMovementTableConfig({ response: result })
            ),
            catchError((error) => {
              const actions = [
                MovementsActionTypes.loadMovementTableConfig({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("TableConfig.LoadError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Movements Table Config
  saveMovementsTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MovementsActionTypes.saveMovementTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            MovementsActionTypes.setLoadingEndState(),
            MessagesActionTypes.setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.SaveSuccess"),
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              MovementsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("TableConfig.SaveError"),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateMovement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MovementsActionTypes.updateMovement),
      exhaustMap((action) =>
        this.movementService.updateMovement(action.movement).pipe(
          map(() => {
            this.router.navigate(["/movements"]);

            return MessagesActionTypes.setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("Movements.movementUpdated"),
                SnackbarType.SUCCESS
              )
            );
          }),
          catchError((error) => {
            const actions = [
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("Movements.movementUpdateFail"),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
}
